import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

const Hero = () => {
  const { t } = useTranslation();

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const letterVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.05,
        duration: 0.6,
        ease: "easeOut"
      }
    })
  };

  return (
    <section id="hero" className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Animated Geometric Background - Deep Blue/Purple Theme */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-950 via-blue-950 to-violet-950">
        
        {/* Animated Network Grid */}
        <svg className="absolute inset-0 w-full h-full opacity-30" viewBox="0 0 1920 1080">
          <defs>
            <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#4f46e5" stopOpacity="0.8"/>
              <stop offset="50%" stopColor="#0ea5e9" stopOpacity="0.9"/>
              <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.8"/>
            </linearGradient>
          </defs>
          
          {/* Animated connection lines */}
          <motion.line
            x1="0" y1="200" x2="1920" y2="300"
            stroke="url(#lineGradient)"
            strokeWidth="1.5"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 4, delay: 0.5, repeat: Infinity, repeatType: "reverse" }}
          />
          <motion.line
            x1="0" y1="600" x2="1920" y2="500"
            stroke="url(#lineGradient)"
            strokeWidth="1.5"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 3, delay: 1, repeat: Infinity, repeatType: "reverse" }}
          />
          <motion.line
            x1="300" y1="0" x2="400" y2="1080"
            stroke="url(#lineGradient)"
            strokeWidth="1.5"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 5, delay: 2, repeat: Infinity, repeatType: "reverse" }}
          />
          <motion.line
            x1="1200" y1="0" x2="1100" y2="1080"
            stroke="url(#lineGradient)"
            strokeWidth="1.5"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 4.5, delay: 1.5, repeat: Infinity, repeatType: "reverse" }}
          />
        </svg>

        {/* Floating Geometric Shapes */}
        <motion.div
          className="absolute top-20 left-20 w-32 h-32 border border-indigo-400/40 rounded-full"
          animate={{
            rotate: [0, 360],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
        />
        
        <motion.div
          className="absolute top-40 right-32 w-24 h-24 bg-gradient-to-r from-violet-500/30 to-indigo-500/30 rotate-45"
          animate={{
            rotate: [45, 405],
            x: [0, 20, 0],
            y: [0, -20, 0],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />

        <motion.div
          className="absolute bottom-32 left-32 w-16 h-16 border-2 border-purple-400/50"
          animate={{
            rotate: [0, 180, 360],
            borderRadius: ["0%", "50%", "0%"],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />

        <motion.div
          className="absolute bottom-20 right-20 w-20 h-20 bg-gradient-to-r from-blue-500/30 to-violet-500/30 rounded-full"
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />

        {/* Tech-inspired Nodes */}
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className={`absolute w-2 h-2 rounded-full ${
              i % 4 === 0 ? 'bg-indigo-400' : 
              i % 4 === 1 ? 'bg-blue-400' : 
              i % 4 === 2 ? 'bg-violet-400' : 'bg-purple-400'
            }`}
            style={{
              left: `${20 + (i * 15)}%`,
              top: `${20 + (i * 8)}%`,
            }}
            animate={{
              scale: [1, 1.5, 1],
              opacity: [0.5, 0.9, 0.5],
            }}
            transition={{
              duration: 3 + i * 0.5,
              repeat: Infinity,
              delay: i * 0.3,
            }}
          />
        ))}

        {/* Animated Code-like Elements */}
        <motion.div
          className="absolute top-60 left-40 opacity-15"
          animate={{
            opacity: [0.15, 0.4, 0.15],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
          }}
        >
          <div className="font-mono text-indigo-300 text-xs space-y-1">
            <div className="w-16 h-1 bg-indigo-400 rounded"></div>
            <div className="w-12 h-1 bg-blue-400 rounded"></div>
            <div className="w-20 h-1 bg-violet-400 rounded"></div>
            <div className="w-8 h-1 bg-purple-400 rounded"></div>
          </div>
        </motion.div>

        <motion.div
          className="absolute bottom-60 right-40 opacity-15"
          animate={{
            opacity: [0.15, 0.4, 0.15],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            delay: 2,
          }}
        >
          <div className="font-mono text-violet-300 text-xs space-y-1">
            <div className="w-14 h-1 bg-violet-400 rounded"></div>
            <div className="w-18 h-1 bg-indigo-400 rounded"></div>
            <div className="w-10 h-1 bg-blue-400 rounded"></div>
            <div className="w-16 h-1 bg-purple-400 rounded"></div>
          </div>
        </motion.div>

        {/* Particle System */}
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={`particle-${i}`}
            className={`absolute w-1 h-1 rounded-full opacity-70 ${
              i % 5 === 0 ? 'bg-indigo-400' : 
              i % 5 === 1 ? 'bg-blue-400' : 
              i % 5 === 2 ? 'bg-violet-400' : 
              i % 5 === 3 ? 'bg-purple-400' : 'bg-cyan-400'
            }`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -30, 0],
              x: [0, Math.random() * 20 - 10, 0],
              opacity: [0.7, 1, 0.7],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 4 + Math.random() * 3,
              repeat: Infinity,
              delay: Math.random() * 3,
              ease: "easeInOut"
            }}
          />
        ))}
      </div>
      
      {/* Enhanced Color Accents */}
      <div className="absolute inset-0">
        {/* Blue-Purple accent glows */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-indigo-500/15 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-violet-500/15 rounded-full blur-3xl animate-pulse" 
             style={{ animationDelay: '2s' }} />
        
        {/* Professional Grid Pattern with Color */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(79,70,229,0.06)_1px,transparent_1px),linear-gradient(90deg,rgba(79,70,229,0.06)_1px,transparent_1px)] [background-size:40px_40px]" />
      </div>

      {/* Enhanced Animated Background Elements */}
      <div className="absolute inset-0">
        {[...Array(25)].map((_, i) => (
          <motion.div
            key={i}
            className={`absolute w-px h-px rounded-full opacity-40 ${
              i % 3 === 0 ? 'bg-indigo-400' : i % 3 === 1 ? 'bg-violet-400' : 'bg-blue-400'
            }`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              opacity: [0.4, 0.8, 0.4],
              scale: [1, 1.5, 1],
            }}
            transition={{
              duration: Math.random() * 4 + 3,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      {/* Enhanced Geometric Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Professional Blue-Purple Circle */}
        <motion.div
          className="absolute top-32 right-32 w-64 h-64 border border-indigo-500/40 rounded-full shadow-lg"
          animate={{
            rotate: [0, 360],
          }}
          transition={{
            duration: 60,
            repeat: Infinity,
            ease: "linear"
          }}
        >
          <motion.div
            className="absolute top-4 left-4 w-4 h-4 bg-violet-400/80 rounded-full shadow-lg"
            animate={{
              rotate: [0, -360],
            }}
            transition={{
              duration: 30,
              repeat: Infinity,
              ease: "linear"
            }}
          />
        </motion.div>

        {/* Violet Rectangle */}
        <motion.div
          className="absolute bottom-40 left-32"
          animate={{
            y: [0, -10, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <div className="w-20 h-20 border border-violet-400/40 rotate-45 bg-indigo-900/30 shadow-lg" />
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 text-center relative z-10 flex flex-col justify-center items-center min-h-screen">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="w-full max-w-4xl"
        >
          {/* Enhanced Professional Icons */}
          <div className="absolute inset-0 pointer-events-none">
            {/* Code Icon with Indigo Glow */}
            <motion.div
              className="absolute top-20 right-40"
              animate={{
                y: [0, -8, 0],
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <div className="relative">
                <div className="absolute inset-0 bg-indigo-500/25 rounded-full blur-md" />
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" className="text-indigo-300 opacity-50 relative z-10">
                  <path d="M8 18L2 12L8 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 6L22 12L16 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </motion.div>

            {/* Database Icon with Violet Glow */}
            <motion.div
              className="absolute bottom-60 left-40"
              animate={{
                x: [0, 5, 0],
              }}
              transition={{
                duration: 7,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <div className="relative">
                <div className="absolute inset-0 bg-violet-500/25 rounded-full blur-md" />
                <svg width="35" height="35" viewBox="0 0 24 24" fill="none" className="text-violet-300 opacity-50 relative z-10">
                  <ellipse cx="12" cy="5" rx="9" ry="3" stroke="currentColor" strokeWidth="2"/>
                  <path d="M3 5V19C3 20.1046 7.02944 21 12 21C16.9706 21 21 20.1046 21 19V5" stroke="currentColor" strokeWidth="2"/>
                  <path d="M3 12C3 13.1046 7.02944 14 12 14C16.9706 14 21 13.1046 21 12" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
            </motion.div>

            {/* Settings Icon with Blue Glow */}
            <motion.div
              className="absolute top-60 left-20"
              animate={{
                rotate: [0, 180, 360],
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: "linear"
              }}
            >
              <div className="relative">
                <div className="absolute inset-0 bg-blue-500/25 rounded-full blur-lg" />
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" className="text-blue-300 opacity-40 relative z-10">
                  <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                  <path d="M12 1v6m0 6v6m11-7h-6m-6 0H1" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
            </motion.div>
          </div>

          {/* Enhanced HOKS Logo - 3x bigger than footer */}
          <motion.div 
            className="text-8xl md:text-9xl font-bold mb-8 relative inline-block"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            whileHover={{ scale: 1.02 }}
          >
            <motion.span
              style={{
                background: "linear-gradient(135deg, #ffffff, #a78bfa, #60a5fa, #8b5cf6)",
                backgroundSize: "300% 100%",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text"
              }}
              animate={{
                backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"]
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              HOKS
            </motion.span>
            
            {/* Logo Glow Effect */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-indigo-500/30 to-violet-500/30 rounded-lg blur-xl opacity-0"
              whileHover={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            />
          </motion.div>

          {/* Enhanced Subtitle - moved closer to logo */}
          <motion.h2 
            className="text-xl md:text-2xl mb-6 font-light relative"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.4 }}
            style={{
              background: "linear-gradient(135deg, #e2e8f0, #a78bfa, #60a5fa)",
              backgroundSize: "200% 100%",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text"
            }}
            animate={{
              backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"]
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <motion.span className="inline-block">
              {t('hero.subtitle')}
            </motion.span>
          </motion.h2>

          {/* Description - moved closer */}
          <motion.div
            className="overflow-hidden mb-8"
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <motion.p 
              className="text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              {t('hero.description')}
            </motion.p>
          </motion.div>

          {/* Enhanced CTA Button - moved closer */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 1 }}
            className="relative"
          >
            <motion.button
              onClick={scrollToContact}
              className="relative overflow-hidden bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white px-8 py-4 rounded-lg text-lg font-medium transition-all duration-300 border border-indigo-500 shadow-lg shadow-indigo-500/30"
              whileHover={{ 
                scale: 1.02,
                boxShadow: "0 20px 25px -5px rgba(79, 70, 229, 0.5), 0 10px 10px -5px rgba(79, 70, 229, 0.5)"
              }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-400 to-violet-400 opacity-0 hover:opacity-20 transition-opacity duration-300" />
              <span className="relative z-10 flex items-center space-x-2">
                <span>{t('hero.cta')}</span>
                <motion.span
                  animate={{ x: [0, 3, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  →
                </motion.span>
              </span>
            </motion.button>
          </motion.div>
        </motion.div>
      </div>

      {/* Enhanced Scroll Indicator - Slightly left of center */}
      <motion.div 
        className="absolute bottom-32 md:bottom-24 z-20"
        style={{ 
          left: "49%", 
          transform: "translateX(-50%)",
          marginLeft: 0, 
          marginRight: 0 
        }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 1.2 }}
      >
        <motion.div
          className="flex flex-col items-center cursor-pointer group"
          onClick={() => scrollToSection('about')}
          whileHover={{ scale: 1.05 }}
        >
          <motion.span 
            className="text-indigo-300 text-sm mb-2 opacity-70 group-hover:opacity-100 transition-opacity"
          >
            Scroll
          </motion.span>
          
          <motion.div
            className="w-6 h-10 border border-indigo-400 rounded-full flex justify-center relative overflow-hidden"
            animate={{ 
              borderColor: ["#a78bfa", "#60a5fa", "#8b5cf6", "#a78bfa"]
            }}
            transition={{ duration: 4, repeat: Infinity }}
          >
            <motion.div 
              className="w-1 h-3 bg-violet-400 rounded-full mt-2"
              animate={{ 
                y: [0, 16, 0],
                opacity: [1, 0, 1]
              }}
              transition={{ 
                duration: 2, 
                repeat: Infinity, 
                ease: "easeInOut" 
              }}
            />
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );

  function scrollToSection(sectionId) {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }
};

export default Hero;